import React, { useState } from 'react';
import { 
    Shield, Clock, Globe, Monitor, MapPin, User, Mail, 
    ChevronDown, ChevronUp, Eye, FileText, Download
} from 'lucide-react';
import { formatAuditTrailForDisplay } from '../../utils/signatureAudit';

/**
 * SignatureAuditDisplay - Displays e-signature audit trail for admin review
 * Shows signature image and comprehensive audit data for UK legal compliance
 */
const SignatureAuditDisplay = ({ 
    signatureData, 
    signatureAudit, 
    signerName,
    signerEmail,
    showFullDetails = false 
}) => {
    const [isExpanded, setIsExpanded] = useState(showFullDetails);

    // Parse audit trail if it's a string
    const audit = typeof signatureAudit === 'string' 
        ? JSON.parse(signatureAudit) 
        : signatureAudit;

    if (!signatureData && !audit) {
        return (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                <p className="text-gray-500">No signature data available</p>
            </div>
        );
    }

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('en-GB', {
            dateStyle: 'full',
            timeStyle: 'long'
        });
    };

    return (
        <div className="space-y-4">
            {/* Signature Image */}
            {signatureData && (
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Digital Signature
                    </h4>
                    <div className="bg-gray-50 border border-gray-100 rounded-lg p-4 inline-block">
                        {signatureData.startsWith('data:') || signatureData.startsWith('http') ? (
                            <img 
                                src={signatureData} 
                                alt="Signature" 
                                className="max-h-24 max-w-full object-contain mix-blend-multiply"
                            />
                        ) : (
                            <p className="text-xl font-signature text-gray-800 px-4 py-2">
                                {signatureData}
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* Audit Trail Summary */}
            {audit && (
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                        <div className="flex items-center gap-2">
                            <Shield className="w-4 h-4 text-green-600" />
                            <span className="font-semibold text-gray-800 text-sm">
                                Digital Signature Audit Trail
                            </span>
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                Verified
                            </span>
                        </div>
                        {isExpanded ? (
                            <ChevronUp className="w-4 h-4 text-gray-500" />
                        ) : (
                            <ChevronDown className="w-4 h-4 text-gray-500" />
                        )}
                    </button>

                    {isExpanded && (
                        <div className="p-4 space-y-4">
                            {/* Signer Information */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                                    <User className="w-5 h-5 text-gray-400 mt-0.5" />
                                    <div>
                                        <p className="text-xs text-gray-500 uppercase tracking-wide">Signer Name</p>
                                        <p className="text-sm font-medium text-gray-800">
                                            {audit.signer_name || signerName || 'N/A'}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                                    <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                                    <div>
                                        <p className="text-xs text-gray-500 uppercase tracking-wide">Signer Email</p>
                                        <p className="text-sm font-medium text-gray-800">
                                            {audit.signer_email || signerEmail || 'N/A'}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Timestamp */}
                            <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                                <Clock className="w-5 h-5 text-blue-500 mt-0.5" />
                                <div>
                                    <p className="text-xs text-blue-600 uppercase tracking-wide">Signed At (UTC)</p>
                                    <p className="text-sm font-medium text-blue-800">
                                        {formatDate(audit.signed_at)}
                                    </p>
                                </div>
                            </div>

                            {/* Location & IP */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                                    <Globe className="w-5 h-5 text-gray-400 mt-0.5" />
                                    <div>
                                        <p className="text-xs text-gray-500 uppercase tracking-wide">IP Address</p>
                                        <p className="text-sm font-medium text-gray-800 font-mono">
                                            {audit.ip_address || 'N/A'}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                                    <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                                    <div>
                                        <p className="text-xs text-gray-500 uppercase tracking-wide">Location</p>
                                        <p className="text-sm font-medium text-gray-800">
                                            {audit.city && audit.country 
                                                ? `${audit.city}, ${audit.country}`
                                                : audit.country || 'N/A'
                                            }
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Device Information */}
                            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                                <Monitor className="w-5 h-5 text-gray-400 mt-0.5" />
                                <div className="flex-1">
                                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Device Information</p>
                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                        <div>
                                            <span className="text-gray-500">Browser:</span>{' '}
                                            <span className="text-gray-800">{audit.browser || 'N/A'}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500">OS:</span>{' '}
                                            <span className="text-gray-800">{audit.operating_system || 'N/A'}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500">Device:</span>{' '}
                                            <span className="text-gray-800">{audit.device_type || 'N/A'}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500">Screen:</span>{' '}
                                            <span className="text-gray-800">{audit.screen_resolution || 'N/A'}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* User Agent (Collapsible) */}
                            {audit.user_agent && (
                                <details className="group">
                                    <summary className="cursor-pointer text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1">
                                        <Eye className="w-3 h-3" />
                                        View Full User Agent String
                                    </summary>
                                    <div className="mt-2 p-2 bg-gray-100 rounded text-xs font-mono text-gray-600 break-all">
                                        {audit.user_agent}
                                    </div>
                                </details>
                            )}

                            {/* Compliance Notice */}
                            <div className="mt-4 pt-4 border-t border-gray-200">
                                <p className="text-xs text-gray-500 flex items-center gap-1">
                                    <Shield className="w-3 h-3" />
                                    This digital signature meets UK Electronic Communications Act 2000 requirements
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SignatureAuditDisplay;
